/**
 * The application's notion of "today".
 *
 * Every screen reads the current day from here rather than calling `Date.now` directly, which
 * keeps them consistent within a render and makes the day overridable. The override exists for
 * development: stepping the clock is the only practical way to exercise future sessions, term
 * boundaries, and long histories without waiting for real time to pass.
 */

import { useSyncExternalStore } from 'react';

import { today } from '../shared/dates.js';
import type { IsoDate } from '../shared/types.js';

/** Where an override is kept. Session storage, so it never outlives the browser tab. */
const STORAGE_KEY = 'anwesenheit.simulated-day';

/** Reads a stored override, or `null` when there is none. */
function readStoredOverride(): IsoDate | null {
  const stored = globalThis.sessionStorage?.getItem(STORAGE_KEY) ?? null;
  return stored === null || stored === '' ? null : stored;
}

/** An observable calendar day that can be pinned to a fixed value. */
class DayClock {
  private override: IsoDate | null = readStoredOverride();

  private listeners = new Set<() => void>();

  /** Subscribes to day changes; the returned function unsubscribes. */
  subscribe = (listener: () => void): (() => void) => {
    this.listeners.add(listener);
    return () => { this.listeners.delete(listener); };
  };

  /** Returns the current day, honouring an override when one is set. */
  getDay = (): IsoDate => this.override ?? today();

  /** Returns the override, or `null` when the clock follows the real date. */
  getOverride = (): IsoDate | null => this.override;

  /**
   * Pins the clock to `day`, or releases it when `day` is `null`.
   *
   * Every subscriber re-renders, and every cached derivation keyed on the day is recomputed.
   */
  setOverride(day: IsoDate | null): void {
    this.override = day;
    if (day === null) globalThis.sessionStorage?.removeItem(STORAGE_KEY);
    else globalThis.sessionStorage?.setItem(STORAGE_KEY, day);
    for (const listener of this.listeners) listener();
  }
}

/** The process-wide clock. */
export const dayClock = new DayClock();

/** Returns the current day and re-renders the caller when it changes. */
export function useToday(): IsoDate {
  return useSyncExternalStore(dayClock.subscribe, dayClock.getDay, dayClock.getDay);
}

/** Returns the current override and re-renders the caller when it changes. */
export function useDayOverride(): IsoDate | null {
  return useSyncExternalStore(dayClock.subscribe, dayClock.getOverride, dayClock.getOverride);
}
